import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { SerializedError } from '@reduxjs/toolkit'

/**
 * Extract the backend error code from an RTK Query error.
 *
 * The backend envelope (BaseResponse) wraps a ResponseStatus that may expose
 * the code either at the top level or nested under `status` depending on the
 * endpoint, so we check both shapes.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as FetchBaseQueryError).data
    if (data && typeof data === 'object') {
      const code =
        (data as { errorCode?: unknown }).errorCode ??
        (data as { status?: { errorCode?: unknown } }).status?.errorCode
      if (typeof code === 'string') return code
    }
  }
  return undefined
}

/**
 * Resolve a human-readable message from an RTK Query error, falling back to the
 * provided default when nothing usable is found.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as FetchBaseQueryError).data
    if (typeof data === 'string' && data.trim()) return data
    if (data && typeof data === 'object') {
      const msg =
        (data as { message?: unknown }).message ??
        (data as { status?: { message?: unknown } }).status?.message ??
        (data as { errorDescription?: unknown }).errorDescription ??
        (data as { status?: { errorDescription?: unknown } }).status
          ?.errorDescription
      if (typeof msg === 'string' && msg.trim()) return msg
    }
  }
  if (error && typeof error === 'object' && 'message' in (error as SerializedError)) {
    const m = (error as SerializedError).message
    if (m) return String(m)
  }
  return fallback
}
